import { IconButton } from '@coop/components';
import { TrashIcon } from '@coop/icons';
import { useMemo } from 'react';

import type { ApiProduct } from '../../../../../models/ecommerce/product/ApiProduct';
import { ProductMissingItemInfo } from '../../../../common/components/organisms/MissingItemBody';
import { QuantityStepper } from '../../../../common/components/QuantityStepper';
import RenderableChildrenWrapper from '../../../../common/components/RenderableChildrenWrapper';
import { useAppSelector } from '../../../../common/hooks/useThunkDispatch';
import { useIsUserAMember } from '../../../../common/hooks/useUserProfile';
import type { PriceType } from '../../../../common/models/priceData/priceData';
import { featureFlagsSelectors } from '../../../../common/selectors/featureFlagsSelectors';
import { useIsB2BRoute } from '../../../../common/utils/b2bUtils';
import SharedBetweenRecipesPopup from '../../../../recipe/components/SharedBetweenRecipesPopup/SharedBetweenRecipesPopup';
import type { RecipeItemState, RecipeSectionType } from '../../../../recipe/models';
import { CartItemLayout } from '../../CartItemLayout';
import { ProductListingItemNew as ProductListingItem } from '../ProductListingItem';
import {
    getRecipeProductListingItemA11yText,
    mapToRecipeProductListingItemData,
} from './RecipeProductListingItem.utils';
import RecipeProductListingItemPriceLabels from './RecipeProductListingItemPriceLabels';

interface RecipeProductListingItemProps {
    recipeItem: RecipeItemState;
    product?: ApiProduct;
    purchasable: boolean;
    additionalClassNames?: string;
    priceType: PriceType;
    recipeSectionType: RecipeSectionType;
    handleRemoveItem: () => void;
    handleQuantityChange: (newQuantity: number) => void;
    withBuyButton?: boolean;
    substitutesTriggerSlot?: React.ReactNode;
}

const RecipeProductListingItem = ({
    recipeItem,
    product,
    purchasable,
    additionalClassNames,
    priceType,
    recipeSectionType,
    handleRemoveItem,
    handleQuantityChange,
    withBuyButton,
    substitutesTriggerSlot,
}: React.PropsWithChildren<RecipeProductListingItemProps>) => {
    const variant = recipeItem.selectedVarianceCode
        ? product?.variants?.find((_variant) => _variant.id === recipeItem.selectedVarianceCode)
        : undefined;

    const a11yTextType = 'ariaHidden';

    const productData = mapToRecipeProductListingItemData(recipeItem, variant, product);

    const isUserMember = useIsUserAMember();

    const isB2B = useIsB2BRoute();
    const arePILPricesEnabled = useAppSelector(featureFlagsSelectors.selectPILPricesEnabled);
    const isPILPricesLogicEnabled = useAppSelector(
        featureFlagsSelectors.selectPILPricesLogicEnabled,
    );

    const a11yLabel = useMemo(
        () =>
            getRecipeProductListingItemA11yText({
                product: productData,
                recipeItem,
                priceType,
                a11yTextType,
                purchasable,
                isB2B,
                isUserMember,
                arePILPricesEnabled,
                isPILPricesLogicEnabled,
            }),
        [
            productData,
            recipeItem,
            priceType,
            a11yTextType,
            purchasable,
            isB2B,
            isUserMember,
            arePILPricesEnabled,
            isPILPricesLogicEnabled,
        ],
    );

    const showSubstituteTrigger = !!recipeItem.substituteProductsAmount;

    const showPopup =
        !!recipeItem.belongsToRecipes?.length &&
        (recipeSectionType === 'shared' || recipeSectionType === 'common');

    const canBeRemoved = recipeSectionType === 'recipe' || recipeSectionType === 'shared'; // Only products in shared and normal recipes can be removed, not common ones

    return (
        <CartItemLayout.Container>
            <ProductListingItem.Container
                isALink
                product={productData}
                a11yLabel={a11yLabel}
                a11yType={a11yTextType}
                className={additionalClassNames}
            >
                <CartItemLayout.Bottom>
                    {purchasable ? (
                        <CartItemLayout.PricingLayout>
                            <ProductListingItem.PricingContainer>
                                <ProductListingItem.PrimaryPricing>
                                    <RecipeProductListingItemPriceLabels
                                        type="primary"
                                        product={productData}
                                        recipeItem={recipeItem}
                                        priceType={priceType}
                                        a11yTextType={a11yTextType}
                                        isB2B={isB2B}
                                        isUserMember={isUserMember}
                                        arePILPricesEnabled={arePILPricesEnabled}
                                        isPILPricesLogicEnabled={isPILPricesLogicEnabled}
                                    />
                                </ProductListingItem.PrimaryPricing>
                                <ProductListingItem.SecondaryPricing>
                                    <RecipeProductListingItemPriceLabels
                                        type="secondary"
                                        product={productData}
                                        recipeItem={recipeItem}
                                        priceType={priceType}
                                        a11yTextType={a11yTextType}
                                        isB2B={isB2B}
                                        isUserMember={isUserMember}
                                        arePILPricesEnabled={arePILPricesEnabled}
                                        isPILPricesLogicEnabled={isPILPricesLogicEnabled}
                                    />
                                </ProductListingItem.SecondaryPricing>
                            </ProductListingItem.PricingContainer>
                            <CartItemLayout.TotalPricing>
                                <CartItemLayout.TotalPriceGroup>
                                    <RecipeProductListingItemPriceLabels
                                        type="total"
                                        product={productData}
                                        recipeItem={recipeItem}
                                        priceType={priceType}
                                        a11yTextType={a11yTextType}
                                        isB2B={isB2B}
                                        isUserMember={isUserMember}
                                        arePILPricesEnabled={arePILPricesEnabled}
                                        isPILPricesLogicEnabled={isPILPricesLogicEnabled}
                                    />
                                </CartItemLayout.TotalPriceGroup>
                                {(!!recipeItem.quantity || recipeItem.quantity === 0) && ( // 0 is allowed for good to have, !!0 = false, hence additional check for 0
                                    <div style={{ maxWidth: 150, minWidth: 140 }}>
                                        <QuantityStepper
                                            theme="secondary"
                                            size={48}
                                            quantity={recipeItem.quantity}
                                            setQuantity={handleQuantityChange}
                                            withBuyButton={withBuyButton}
                                            fullWidth
                                            buyButtonText="Lägg till"
                                            inputLabel={`Antal av ${productData.name}`}
                                            addButtonLabel={`Öka antal av ${productData.name}`}
                                            removeButtonLabel={`Minska antal av ${productData.name}`}
                                        />
                                    </div>
                                )}
                            </CartItemLayout.TotalPricing>
                        </CartItemLayout.PricingLayout>
                    ) : (
                        <div />
                    )}

                    <RenderableChildrenWrapper // Render Separator only if anything renderable is underneath
                        render={(ch) => (
                            <>
                                <CartItemLayout.Separator />
                                {ch}
                            </>
                        )}
                    >
                        {purchasable ? (
                            (showSubstituteTrigger || showPopup) && (
                                <div className="u-flex u-flexGap8">
                                    {showSubstituteTrigger && substitutesTriggerSlot}
                                    {showPopup && (
                                        <SharedBetweenRecipesPopup
                                            sectionType={recipeSectionType}
                                            belongsToRecipes={recipeItem.belongsToRecipes}
                                        />
                                    )}
                                </div>
                            )
                        ) : (
                            <div className="u-flex u-flexJustifyStart u-flexAlignCenter u-flexGap20">
                                <ProductMissingItemInfo />
                                <div className="u-flex u-flexGap12">
                                    {showSubstituteTrigger && substitutesTriggerSlot}
                                    {showPopup && (
                                        <SharedBetweenRecipesPopup
                                            sectionType={recipeSectionType}
                                            belongsToRecipes={recipeItem.belongsToRecipes}
                                        />
                                    )}
                                    {canBeRemoved && (
                                        <IconButton
                                            icon={TrashIcon}
                                            label="Ta bort"
                                            size={32}
                                            theme="soft"
                                            onClick={handleRemoveItem}
                                        />
                                    )}
                                </div>
                            </div>
                        )}
                    </RenderableChildrenWrapper>
                </CartItemLayout.Bottom>
            </ProductListingItem.Container>
        </CartItemLayout.Container>
    );
};

export default RecipeProductListingItem;
